// Emoji reaction bar — pure view. Shows the target's current reactions as
// toggle chips (highlighted when `mine`) plus a picker to add one from the
// allowlist. Used on comments, replies, and the proposal.
import { useState } from "react";
import { REACTION_EMOJI, type ReactionSummary } from "./proposalsApi.js";
import { useTranslation } from "../i18n";

export function ReactionBar({
  reactions,
  onToggle,
  disabled = false,
}: {
  readonly reactions: readonly ReactionSummary[];
  readonly onToggle: (emoji: string) => void;
  readonly disabled?: boolean;
}) {
  const { t } = useTranslation("proposals");
  const [picking, setPicking] = useState(false);

  return (
    <div className="flex flex-wrap items-center gap-1">
      {reactions.map((r) => (
        <button
          key={r.emoji}
          type="button"
          disabled={disabled}
          aria-pressed={r.mine}
          onClick={() => onToggle(r.emoji)}
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs disabled:opacity-50 ${
            r.mine
              ? "border-sky-500/50 bg-sky-500/15 text-sky-200"
              : "border-slate-700 bg-slate-800/60 text-slate-300 hover:border-slate-600"
          }`}
        >
          <span>{r.emoji}</span>
          <span className="tabular-nums">{r.count}</span>
        </button>
      ))}

      <div className="relative">
        <button
          type="button"
          disabled={disabled}
          aria-label={t("reaction.add")}
          onClick={() => setPicking((v) => !v)}
          className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-slate-700 bg-slate-800/60 text-xs text-slate-400 hover:border-slate-600 hover:text-slate-200 disabled:opacity-50"
        >
          ＋
        </button>
        {picking && (
          <div
            role="menu"
            className="absolute z-10 mt-1 flex gap-0.5 rounded-md border border-slate-700 bg-slate-900 p-1 shadow-lg"
          >
            {REACTION_EMOJI.map((e) => (
              <button
                key={e}
                type="button"
                role="menuitem"
                aria-label={t("reaction.react", { emoji: e })}
                onClick={() => {
                  onToggle(e);
                  setPicking(false);
                }}
                className="rounded p-1 text-sm hover:bg-slate-700"
              >
                {e}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
